import getConfig from 'next/config'
import { useEffect, useState, useContext } from 'react'

import { EXTERNAL_LINKS, PATHS, L_STORAGE_KEYS } from '@src/consts'
import {
  useFetchUserMe,
  useFetchSsoJwt,
  homePagePath,
  useCustomRouter,
  useLoginTwoFactorAuthEmail,
} from '@src/hooks'
import { usePostCanaryRoutingCheck } from '@src/hooks/queries/canary/mutations/use-post-canary-routing-check'
import { TfaTokenContext } from '@src/pages/_app.page'
import {
  lStorage,
  toAuthenticationType,
  getUser,
  getMailAddress,
} from '@src/utils'
import { isOAuthRequest } from '@src/utils/oauth/oauth'

import { UseSigninEmailMfa } from './type'
import { STAGING_API_ENDPOINT } from '../use-signin/const'

export function useSigninEmailMfa({ setLoginErrors }: UseSigninEmailMfa) {
  const { router, isString } = useCustomRouter()
  const query = router.query
  const queryString =
    typeof window !== 'undefined' ? window.location.search : ''
  const tfaTokenContext = useContext(TfaTokenContext)
  const { publicRuntimeConfig } = getConfig()
  const { API_ENDPOINT } = publicRuntimeConfig
  const isConnectedLocalToStg =
    API_ENDPOINT === STAGING_API_ENDPOINT &&
    process.env.NODE_ENV !== 'production'
  const [enabledFetchUserMe, setEnabledFetchUserMe] = useState(false)
  const [freshdeskNonce, setFreshdeskNonce] = useState('')

  const loginEmailMfaMutation = useLoginTwoFactorAuthEmail()
  const fetchedUserMe = useFetchUserMe({ enabled: enabledFetchUserMe })
  const fetchSsoJwt = useFetchSsoJwt({ nonce: freshdeskNonce })
  const canaryRoutingCheckMutation = usePostCanaryRoutingCheck()

  function handleSubmitEmailMfa(code: string) {
    loginEmailMfaMutation.mutate(
      {
        loginWithTwoFactorAuthEmailForm: {
          emailAddress: getMailAddress() ?? '',
          code,
          tfaToken: tfaTokenContext?.tfaToken ?? '',
        },
      },
      {
        onSuccess: ({ data }) => {
          if (isConnectedLocalToStg && data.token) {
            lStorage.setItem({ key: L_STORAGE_KEYS.TOKEN, value: data.token })
          }
          setEnabledFetchUserMe(true)
          const refererService = query.refererService as string
          const nonce = query.nonce as string
          if (refererService === 'freshdesk') {
            setFreshdeskNonce(nonce)
          }
        },
        onError: (error) => {
          const errors = error.data?.errors
          if (errors?.code) {
            setLoginErrors(errors.code)
          } else if (errors?.tfaToken) {
            setLoginErrors(errors.tfaToken)
          } else {
            setLoginErrors(['不明なエラーが発生しました'])
          }
        },
      }
    )
  }

  useEffect(() => {
    if (fetchedUserMe.isSuccess) {
      const rawUserMe = fetchedUserMe.data.data
      const convertedResponse = toAuthenticationType(rawUserMe)
      lStorage.setItem({ key: L_STORAGE_KEYS.USER, value: convertedResponse })
      const refererService = query.refererService as string
      if (refererService === 'freshdesk') return
      moveToNextUrl()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedUserMe.isSuccess])

  useEffect(() => {
    if (fetchSsoJwt.isSuccess) {
      const token = fetchSsoJwt.data.data.token
      window.location.href = EXTERNAL_LINKS.FRESHDESK.LINK({
        token,
        queryString,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSsoJwt.isSuccess])

  function moveToNextUrl() {
    const user = getUser()
    const backUrl = isString(query.backUrl) ? query.backUrl : ''
    if (!user) return

    canaryRoutingCheckMutation
      .mutateAsync()
      .then(() => executePostLoginRedirect())
      .catch(() => executePostLoginRedirect())

    function executePostLoginRedirect() {
      if (!user) return
      if (user.isTempPassword) {
        router.push({ pathname: PATHS.REGISTER_PASSWORD })
      } else if (backUrl) {
        window.location.replace(backUrl)
      } else if (isOAuthRequest()) {
        router.push({ pathname: PATHS.SIGNIN_GRANT_CODE })
      } else {
        homePagePath(user)
      }
    }
  }

  return {
    handleSubmitEmailMfa,
    isSubmittingEmailMfa: loginEmailMfaMutation.isPending,
  }
}
